<template lang="pug">
el-dialog(
  title="Select Organizations"
  top="5vh"
  width="60%"
  class="el-dialog__max-w-700"
  :close-on-click-modal="false"
  v-model="dialogVisible"
)
  .organizations-selector
    .organizations-selector-header
      el-input(
        v-model="searchTerm"
        placeholder="Search organizations"
        @input="debouncedSearch"
        clearable
      )
        template(#suffix)
          img(src="@/assets/icons/searchIcon.svg")

    .organizations-list(v-loading="loading")
      .empty-state(v-if="!organizations.length && !loading")
        p No organizations found

      .organizations-list-view(v-else)
        .organization-list-item(
          v-for="organization in organizations"
          :key="organization.id"
          :class="{ 'selected': isSelected(organization.id) }"
          @click="toggleSelection(organization.id)"
        )
          .organization-list-item-content
            .organization-thumbnail-small
              img(
              v-if="organization.avatar"
              :src="organization.avatar"
            )
              .no-image(v-else)
            .organization-details
              .organization-title {{ organization.name }}

    .pagination-controls(v-if="organizations.length")
      .simple-pagination.d-flex.align-items-center
        .page-nav.d-flex
          el-button(
            @click="handleCurrentChange(currentPage - 1)"
            :disabled="currentPage === 1 || loading"
            size="small"
          ) Prev
          .page-numbers.mx-2 {{ currentPage }}
          el-button(
            @click="handleCurrentChange(currentPage + 1)"
            :disabled="!hasMorePages || loading"
            size="small"
          ) Next
        .page-info.ms-3(v-if="organizations.length > 0")
          | Showing {{ startItem }}-{{ endItem }}

  template(#footer)
    .dialog-footer
      el-button(@click="handleCancel") Cancel
      el-button(type="primary" @click="handleConfirm" :disabled="!selectedOrganizations.length")
        | Confirm ({{ selectedOrganizations.length }})
</template>

<script>
import {
  defineComponent, ref, computed, watch, onMounted,
} from 'vue';
import { ElMessage } from 'element-plus';
import { findOrganizationsV2 } from '../../../../../graphs/organization.ts';
import { makeRequest } from '../../../../../graphs/index.ts';

// Using a simple debounce implementation instead of external dependency
function debounce(func, wait) {
  let timeout;
  return function (...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
}

export default defineComponent({
  name: 'DialogSelectOrganizations',
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    preSelectedOrganizations: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['update:modelValue', 'confirm'],
  setup(props, { emit }) {
    const dialogVisible = computed({
      get: () => props.modelValue,
      set: (value) => emit('update:modelValue', value),
    });

    const organizations = ref([]);
    const loading = ref(false);
    const searchTerm = ref('');
    const currentPage = ref(1);
    const pageSize = ref(20);
    const hasMorePages = ref(true);
    const selectedOrganizations = ref([...props.preSelectedOrganizations]);
    const selectedOrganizationsMap = ref({});

    // Update the map based on the selectedOrganizations array
    const updateSelectedOrganizationsMap = () => {
      const newMap = {};
      selectedOrganizations.value.forEach((id) => {
        newMap[id] = true;
      });
      selectedOrganizationsMap.value = newMap;
    };

    // Watch for changes to preSelectedOrganizations
    watch(() => props.preSelectedOrganizations, (newVal) => {
      selectedOrganizations.value = [...newVal];
      // Update the map
      updateSelectedOrganizationsMap();
    });

    // Define fetchOrganizations function
    const fetchOrganizations = async () => {
      loading.value = true;
      try {
        const result = await makeRequest(
          'query',
          findOrganizationsV2,
          {
            baseInput: {
              page: currentPage.value,
              pageSize: pageSize.value,
              query: searchTerm.value || undefined,
              // statuses: 'Active', // Only show active organizations
              excludeUnclaimed: true,
            },
          },
          null,
        );

        if (result?.data?.findOrganizationsV2) {
          organizations.value = result.data.findOrganizationsV2;
          // Check if we received a full page of results
          // If we got less than pageSize items, there are no more pages
          hasMorePages.value = result.data.findOrganizationsV2.length === pageSize.value;
        }
      } catch (error) {
        console.error('Error fetching organizations:', error);
        ElMessage.error('Failed to load organizations');
      } finally {
        loading.value = false;
      }
    };

    // Watch for dialog visibility to fetch data and reset selections when opened
    watch(() => props.modelValue, (isOpen) => {
      if (isOpen) {
        selectedOrganizations.value = [...props.preSelectedOrganizations];
        updateSelectedOrganizationsMap();
        fetchOrganizations();
      }
    });

    const debouncedSearch = debounce(() => {
      // Reset pagination when searching
      currentPage.value = 1;
      hasMorePages.value = true; // Reset to default
      fetchOrganizations();
    }, 300);

    const handleCurrentChange = (page) => {
      if (page < 1 || (page > currentPage.value && !hasMorePages.value)) {
        return;
      }
      currentPage.value = page;
      fetchOrganizations();
    };

    // Computed properties for pagination display
    // Calculate first item number in current page
    const startItem = computed(() => {
      const offset = (currentPage.value - 1) * pageSize.value;
      return offset + 1;
    });
    // Calculate last item number in current page
    const endItem = computed(() => {
      const offset = (currentPage.value - 1) * pageSize.value;
      return offset + organizations.value.length;
    });

    const isSelected = (id) => selectedOrganizations.value.includes(id);

    const toggleSelection = (id) => {
      const index = selectedOrganizations.value.indexOf(id);
      if (index === -1) {
        selectedOrganizations.value.push(id);
        selectedOrganizationsMap.value[id] = true;
      } else {
        selectedOrganizations.value.splice(index, 1);
        selectedOrganizationsMap.value[id] = false;
      }
    };

    // Watch for changes to the checkbox map
    watch(selectedOrganizationsMap, (newMap) => {
      const newSelectedIds = Object.keys(newMap).filter((id) => newMap[id]);
      selectedOrganizations.value = newSelectedIds;
    }, { deep: true });

    const handleConfirm = () => {
      // Get full organization objects for the selected IDs
      const selectedOrganizationObjects = organizations.value.filter((org) => selectedOrganizations.value.includes(org.id));

      emit('confirm', selectedOrganizations.value, selectedOrganizationObjects);
      dialogVisible.value = false;
    };

    const handleCancel = () => {
      dialogVisible.value = false;
    };

    onMounted(() => {
      // Initialize the selected organizations map
      updateSelectedOrganizationsMap();

      // Only fetch if dialog is open on mount
      if (props.modelValue) {
        fetchOrganizations();
      }
    });

    return {
      dialogVisible,
      organizations,
      loading,
      searchTerm,
      currentPage,
      pageSize,
      hasMorePages,
      selectedOrganizations,
      selectedOrganizationsMap,
      startItem,
      endItem,
      debouncedSearch,
      handleCurrentChange,
      isSelected,
      toggleSelection,
      handleConfirm,
      handleCancel,
    };
  },
});
</script>

<style scoped>
.organizations-selector {
  display: flex;
  flex-direction: column;
  height: 60vh;
}

.organizations-selector-header {
  margin-bottom: 20px;
}

.organizations-list {
  flex: 1;
  overflow-y: auto;
  min-height: 300px;
}

.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: #909399;
}

.organizations-list-view {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.organization-list-item {
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 10px;
  cursor: pointer;
  position: relative;
  transition: all 0.3s;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.organization-list-item:hover {
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.organization-list-item.selected {
  border-color: #1EBEAF;
  background-color: rgba(30, 190, 175, 0.05);
}

.organization-list-item-content {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.organization-thumbnail-small {
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 4px;
  flex-shrink: 0;
}

.organization-thumbnail-small img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.no-image {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f5f7fa;
  color: #909399;
}

.organization-details {
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 1;
  min-width: 0;
}

.organization-title {
  font-weight: bold;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.organization-info {
  display: flex;
}

.organization-status {
  font-size: 0.85em;
  color: #67C23A;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.organization-status.inactive {
  color: #909399;
}

.organization-select-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
}

.pagination-controls {
  margin-top: 20px;
  display: flex;
  justify-content: center;
}

.simple-pagination {
  display: flex;
  align-items: center;
}

.page-nav {
  display: flex;
  align-items: center;
}

.page-numbers {
  margin: 0 10px;
  font-weight: bold;
}

.page-info {
  margin-left: 15px;
  color: #606266;
  font-size: 0.9em;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 20px;
}
</style>
